// SEO / JSON-LD helper builders.
export const SITE_URL = "https://pageonelawyers.com";
export const ORG_NAME = "PageOne Lawyers";
export const ORG_LOGO = `${SITE_URL}/favicon.svg`;

export function absoluteUrl(path: string) {
  if (!path) return SITE_URL;
  if (path.startsWith("http")) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

export type Crumb = { label: string; path?: string };

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: ORG_NAME,
    url: `${SITE_URL}/`,
    logo: ORG_LOGO,
    description: "SEO agency built exclusively for law firms across every practice area and US market.",
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "contact@pageonelawyers.com",
        telephone: "+91-98880-66671",
        areaServed: "US",
        availableLanguage: ["English"],
      },
    ],
    sameAs: [],
  };
}

export function legalServiceSchema({
  name,
  description,
  url,
  serviceType,
  areaServed,
}: {
  name: string;
  description: string;
  url: string;
  serviceType?: string;
  areaServed?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    name,
    description,
    url: absoluteUrl(url),
    serviceType: serviceType ?? "Search Engine Optimization for Law Firms",
    provider: {
      "@type": "Organization",
      name: ORG_NAME,
      url: `${SITE_URL}/`,
      logo: ORG_LOGO,
    },
    areaServed: areaServed
      ? { "@type": "Place", name: areaServed }
      : { "@type": "Country", name: "United States" },
  };
}

export function localBusinessSchema({
  location,
  url,
}: {
  location: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${absoluteUrl(url)}#localbusiness`,
    name: `${ORG_NAME} — ${location}`,
    description: `Local SEO and lawyer SEO services for law firms in ${location}.`,
    url: absoluteUrl(url),
    image: ORG_LOGO,
    priceRange: "$$-$$$",
    email: "contact@pageonelawyers.com",
    telephone: "+91-98880-66671",
    areaServed: { "@type": "Place", name: location },
    address: { "@type": "PostalAddress", addressCountry: "US", addressLocality: location },
  };
}

export function breadcrumbSchema(crumbs: Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.label,
      ...(c.path ? { item: absoluteUrl(c.path) } : {}),
    })),
  };
}

export function jsonLdScript(schema: unknown) {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify(schema),
  };
}

/** OG image endpoint that renders a unique per-slug SVG card. */
export function ogImageFor(kind: "service" | "location" | "practice" | "case-study" | "generic", slug: string) {
  return `${SITE_URL}/og/${kind}/${encodeURIComponent(slug)}`;
}